import { useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { Loader2, RotateCcw, RotateCw } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const RATIOS = [["a4p", "A4 portrait", 1 / 1.414], ["a4l", "A4 paysage", 1.414], ["free", "Photo entière", null]];

const loadImg = (src) => new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });

// Crops the rotated image to `area` (pixels of the rotated bounding box, as given by react-easy-crop).
async function render(src, area, rotation) {
  const img = await loadImg(src);
  const rad = (rotation * Math.PI) / 180;
  const bw = Math.abs(Math.cos(rad) * img.width) + Math.abs(Math.sin(rad) * img.height);
  const bh = Math.abs(Math.sin(rad) * img.width) + Math.abs(Math.cos(rad) * img.height);
  const big = document.createElement("canvas");
  big.width = bw; big.height = bh;
  const g = big.getContext("2d");
  g.fillStyle = "#fff"; g.fillRect(0, 0, bw, bh);
  g.translate(bw / 2, bh / 2); g.rotate(rad); g.drawImage(img, -img.width / 2, -img.height / 2);
  const k = Math.min(1, 2200 / Math.max(area.width, area.height));
  const out = document.createElement("canvas");
  out.width = Math.round(area.width * k); out.height = Math.round(area.height * k);
  const o = out.getContext("2d");
  o.fillStyle = "#fff"; o.fillRect(0, 0, out.width, out.height);
  o.drawImage(big, area.x, area.y, area.width, area.height, 0, 0, out.width, out.height);
  return new Promise((res) => out.toBlob(res, "image/jpeg", 0.88));
}

export const PhotoStraightenDialog = ({ file, onDone, onSkip, onCancel }) => {
  const { t } = useI18n();
  const [src, setSrc] = useState("");
  const [natural, setNatural] = useState(1);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [turn, setTurn] = useState(0);
  const [tilt, setTilt] = useState(0);
  const [ratio, setRatio] = useState("a4p");
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!file) { return undefined; }
    const u = URL.createObjectURL(file);
    setSrc(u); setCrop({ x: 0, y: 0 }); setZoom(1); setTurn(0); setTilt(0); setRatio("a4p");
    loadImg(u).then((i) => setNatural(i.width / i.height)).catch(() => {});
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const rotation = turn + tilt;
  const quarter = Math.abs(turn / 90) % 2 === 1;
  const aspect = RATIOS.find((r) => r[0] === ratio)[2] ?? (quarter ? 1 / natural : natural);
  const save = async () => {
    setBusy(true);
    try { onDone(new File([await render(src, area, rotation)], (file.name || "photo").replace(/\.\w+$/, "") + "-recadree.jpg", { type: "image/jpeg" })); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!file} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-3xl" data-testid="straighten-dialog">
        <DialogHeader><DialogTitle>{t("Recadrer et redresser la photo")}</DialogTitle></DialogHeader>
        <div className="relative w-full h-[50vh] bg-muted rounded-md overflow-hidden" data-testid="straighten-area">
          {src ? <Cropper image={src} crop={crop} zoom={zoom} rotation={rotation} aspect={aspect} minZoom={0.5} maxZoom={4} restrictPosition={false}
            onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_, px) => setArea(px)} objectFit="contain" showGrid />
            : <div className="absolute inset-0 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin" /></div>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => setTurn((r) => r - 90)} data-testid="straighten-rotate-left"><RotateCcw className="w-4 h-4" /> 90°</Button>
          <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => setTurn((r) => r + 90)} data-testid="straighten-rotate-right"><RotateCw className="w-4 h-4" /> 90°</Button>
          <div className="inline-flex rounded-full border border-border p-0.5 ml-auto" data-testid="straighten-ratio">
            {RATIOS.map(([k, label]) => (
              <Button key={k} type="button" size="sm" variant={ratio === k ? "default" : "ghost"} className="rounded-full h-7 text-xs" onClick={() => setRatio(k)} data-testid={`straighten-ratio-${k}`}>{t(label)}</Button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-[6rem_1fr_3rem] items-center gap-3 text-xs text-muted-foreground">
          <span>{t("Redresser")}</span>
          <Slider value={[tilt]} min={-20} max={20} step={0.5} onValueChange={(v) => setTilt(v[0])} data-testid="straighten-tilt" />
          <span className="font-mono text-right">{tilt > 0 ? "+" : ""}{tilt}°</span>
          <span>{t("Zoom")}</span>
          <Slider value={[zoom]} min={0.5} max={4} step={0.05} onValueChange={(v) => setZoom(v[0])} data-testid="straighten-zoom" />
          <span className="font-mono text-right">{zoom.toFixed(1)}×</span>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onCancel} data-testid="straighten-cancel">{t("Annuler")}</Button>
          <Button variant="outline" onClick={() => onSkip(file)} disabled={busy} data-testid="straighten-skip">{t("Garder la photo telle quelle")}</Button>
          <Button onClick={save} disabled={!area || busy} className="gap-1" data-testid="straighten-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("Valider la photo")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
